import { Alert, Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import { JSX, useEffect, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { currentTenantId } from '../../../library/brandingCache';
import { toTimeInputValue } from '../../../library/dateTimeHelper';
import { useValidatedForm } from '../../../library/formValidation';
import { ClinicProgramDraft, IProgramDay, toClinicProgramDraft } from '../../../library/models/clinicProgram';
import { ITherapistProgram } from '../../../library/models/therapistProgram';
import { splitApiError } from '../../../library/useApiFormError';
import ResourcesIds from '../../../resources/ResourcesIds';
import { useReactMutation, useReactQuery } from '../../../services/queryService';
import { services } from '../../../services/services';
import { useSnackbar } from '../../layout/snackbarProvider';
import { ProgramWeekEditor, THERAPIST_WEEK_COPY } from '../../program/ProgramWeekEditor';
import { createProgramWeekSchema, ProgramWeekFormValues } from '../../program/programWeekSchema';
import { actionsStyles, alertStyles, blockedSaveStyles, cardStyles, headerBoxStyles, loadingBoxStyles, spacerStyles, statusAlertStyles, subtitleStyles, titleStyles } from './mySchedule.styles';

/**
 * Where a therapist says when they are available.
 *
 * The same seven rows the clinic's administrator sees, with one difference that is the whole
 * point of the screen: **the clinic's own week is the ceiling.** A day the clinic is shut
 * cannot be turned on, the time lists offer only the hours the clinic keeps, and an hour
 * outside them is refused here and again by the API.
 *
 * **Having no schedule is a real state, and the alert at the top says which one you are in.**
 * A therapist who has never opened this page works the clinic's hours and goes on working them
 * as the clinic changes them; one who has saved a week has their own, which the clinic can
 * narrow but never widen. Saving is what moves you from the first to the second, and
 * "use the clinic's hours" is what moves you back -- by *removing* your week rather than by
 * copying the clinic's into it, because a copy would stop following the clinic tomorrow.
 *
 * What this governs is what **patients** can book. A therapist can still put an emergency in
 * their own diary outside these hours, exactly as they can outside the clinic's.
 */
const normalisedDraft = (draft: ClinicProgramDraft): ClinicProgramDraft =>
    draft.map((day: IProgramDay) => ({
        ...day,
        opensAt: toTimeInputValue(day.opensAt),
        closesAt: toTimeInputValue(day.closesAt),
    }));

export const MySchedule = (): JSX.Element => {
    const { t: translate } = useTranslation();
    const { showSnackbar } = useSnackbar();
    const { user } = useAuth();

    const therapistId: number = user?.id ?? 0;

    const { data, isLoading, isError, refetch } = useReactQuery<ITherapistProgram>({
        ...services.therapistProgram.getProgram(therapistId, currentTenantId()),
        // Nobody's program until we know whose. A zero id would ask the API about a therapist
        // that cannot exist and cache the answer under it.
        enabled: therapistId > 0,
    });

    const saveProgram = useReactMutation(services.therapistProgram.saveProgramMutation(therapistId));
    const resetProgram = useReactMutation(services.therapistProgram.resetProgramMutation(therapistId));

    /*
     * The clinic's week, as the cap. Passed to the schema as well as to the editor, so the
     * rule the fields are bounded by and the rule a typed hour is judged against are the same
     * one -- the picker only narrows what it *offers*, and typing is not bounded by anything.
     */
    const bounds: IProgramDay[] | undefined = data?.clinicDays;

    const schema = useMemo(() => createProgramWeekSchema(translate, bounds), [translate, bounds]);

    const {
        control, handleSubmit, reset, setValue, setError,
        formState: { errors, isValid },
    } = useValidatedForm<ProgramWeekFormValues>(schema, {
        defaultValues: { days: toClinicProgramDraft(undefined) },
    });

    /*
     * Seeded from what is in force rather than from the stored rows: for a therapist with no
     * schedule of their own that is the clinic's week, which is exactly what they should be
     * editing from, and for everyone else the two are the same thing.
     */
    useEffect(() => {
        if (data) {
            reset({ days: normalisedDraft(toClinicProgramDraft(data)) });
        }
    }, [data, reset]);

    const current: ClinicProgramDraft =
        (useWatch({ control }) as ProgramWeekFormValues).days ?? [];

    const knownFields: string[] = useMemo(() => Object.keys(schema.fields), [schema]);

    const onValid = (values: ProgramWeekFormValues): void => {
        saveProgram.mutate({ days: values.days }, {
            onSuccess: () => {
                showSnackbar(translate(ResourcesIds.MY_SCHEDULE_SAVE_SUCCESS), 'success');
                refetch();
            },
            onError: (error: Error) => {
                const outcome = splitApiError(error, knownFields, translate,
                    ResourcesIds.MY_SCHEDULE_SAVE_ERROR);

                Object.entries(outcome.fieldErrors).forEach(([field, message]) =>
                    setError(field as keyof ProgramWeekFormValues, { type: 'server', message }));

                if (outcome.formError) {
                    showSnackbar(outcome.formError, 'error');
                }
            },
        });
    };

    const onReset = (): void => {
        resetProgram.mutate(undefined, {
            onSuccess: () => {
                showSnackbar(translate(ResourcesIds.MY_SCHEDULE_RESET_SUCCESS), 'success');
                refetch();
            },
            onError: () => showSnackbar(translate(ResourcesIds.MY_SCHEDULE_SAVE_ERROR), 'error'),
        });
    };

    if (isLoading) {
        return (
            <Box sx={loadingBoxStyles}>
                <CircularProgress size={60} />
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={headerBoxStyles}>
                <Typography variant="h4" sx={titleStyles}>
                    {translate(ResourcesIds.MY_SCHEDULE_TITLE)}
                </Typography>
                <Typography variant="body2" sx={subtitleStyles}>
                    {translate(ResourcesIds.MY_SCHEDULE_DESCRIPTION)}
                </Typography>
            </Box>

            {isError && (
                <Alert severity="error" sx={alertStyles}>
                    {translate(ResourcesIds.MY_SCHEDULE_LOAD_ERROR)}
                </Alert>
            )}

            <Paper sx={cardStyles} elevation={0}>
                <Alert severity="info" sx={statusAlertStyles}>
                    {translate(data?.custom
                        ? ResourcesIds.MY_SCHEDULE_CUSTOM_HOURS
                        : ResourcesIds.MY_SCHEDULE_USING_CLINIC_HOURS)}
                </Alert>

                <ProgramWeekEditor
                    control={control}
                    setValue={setValue}
                    errors={errors}
                    days={current}
                    bounds={bounds}
                    copy={THERAPIST_WEEK_COPY}
                />
            </Paper>

            <Box sx={actionsStyles}>
                {/* Only offered to somebody who has one to give up. */}
                {data?.custom && (
                    <Button onClick={onReset} disabled={resetProgram.isPending}>
                        {translate(ResourcesIds.MY_SCHEDULE_RESET)}
                    </Button>
                )}

                <Box sx={spacerStyles} />

                {/* Beside a disabled Save, never instead of one. */}
                {!isValid && (
                    <Typography variant="body2" sx={blockedSaveStyles}>
                        {translate(ResourcesIds.FORM_BLOCKED_SAVE)}
                    </Typography>
                )}
                <Button
                    variant="contained"
                    onClick={handleSubmit(onValid)}
                    disabled={!isValid || saveProgram.isPending}
                >
                    {translate(ResourcesIds.MY_SCHEDULE_SAVE)}
                </Button>
            </Box>
        </Box>
    );
};
